import React from "react";
import { Link } from "react-router-dom";
import styles from "./Privacy.module.scss";

const Privacy: React.FC = () => {
  return (
    <div className={styles.privacyPage}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Link to="/" className={styles.brand}>
            <span className={styles.cursive}>Hippocampus Gaming</span>
          </Link>
        </header>

        <article className={styles.content}>
          <h1>Privacy Policy</h1>
          <p className={styles.lastUpdated}>Last Updated: August 6, 2026</p>

          <section>
            <h2>About Hippocampus Gaming</h2>
            <p>
              Hippocampus Gaming is a gaming social platform that helps players
              connect, discover communities, and manage gaming identities. This
              Privacy Policy describes how we collect, use, and protect your
              information when you use our platform.
            </p>
          </section>

          <section>
            <h2>Information We Collect</h2>

            <h3>Account Information</h3>
            <ul>
              <li>Email address</li>
              <li>Username and display name</li>
              <li>Profile information (bio, avatar)</li>
              <li>Account preferences and settings</li>
            </ul>

            <h3>Linked Gaming Accounts</h3>
            <p>
              When you choose to link external gaming accounts, we collect
              limited information from those platforms to enable integration
              features:
            </p>
            <ul>
              <li>Steam — profile ID and display name</li>
              <li>Discord — account ID and username</li>
              <li>Epic Games — account ID and display name</li>
              <li>Other supported platforms as they become available</li>
            </ul>

            <h3>User-Generated Content</h3>
            <ul>
              <li>Posts and feed content</li>
              <li>Comments and replies</li>
              <li>Direct messages</li>
              <li>Ratings and reviews</li>
              <li>Community interactions (likes, follows)</li>
            </ul>

            <h3>Technical Information</h3>
            <ul>
              <li>Browser and device information</li>
              <li>Usage analytics</li>
              <li>Security logs</li>
            </ul>
          </section>

          <section>
            <h2>Third-Party Integrations</h2>
            <p>
              Hippocampus Gaming integrates with external gaming platforms
              including Epic Games, Steam, Discord, and other supported services.
              These integrations allow you to link your gaming accounts and
              display your gaming identity across the platform.
            </p>
            <p>
              We only access the minimum data required to provide integration
              features (such as your account ID and display name). Each
              third-party platform has its own privacy policy governing how they
              handle your data:
            </p>
            <ul>
              <li>
                <a
                  href="https://www.epicgames.com/site/en-US/privacypolicy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Epic Games Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="https://store.steampowered.com/privacy_agreement/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Steam Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="https://discord.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Discord Privacy Policy
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2>How We Use Your Information</h2>
            <ul>
              <li>Provide and maintain the platform</li>
              <li>Display your gaming identity and linked accounts</li>
              <li>Enable social features (messaging, following, posts)</li>
              <li>Send notifications you have opted into</li>
              <li>Improve and optimize the platform experience</li>
              <li>Protect against unauthorized access and abuse</li>
            </ul>
          </section>

          <section>
            <h2>Your Rights</h2>

            <h3>Account Deletion</h3>
            <p>
              You may request deletion of your account and all associated data by
              contacting us. Upon deletion, your profile, posts, messages, and
              linked account data will be permanently removed.
            </p>

            <h3>Removing Linked Accounts</h3>
            <p>
              You can unlink any connected gaming account at any time from your
              Settings page. Unlinking removes the stored connection data from
              our platform.
            </p>

            <h3>Contact Us</h3>
            <p>
              For privacy questions, data requests, or account deletion, contact
              us at:{" "}
              <a href="mailto:privacy@hippocampusgaming.com">
                privacy@hippocampusgaming.com
              </a>
            </p>
          </section>

          <section>
            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your
              data, including encrypted connections (HTTPS), secure token
              storage, and access controls. However, no method of transmission
              over the internet is completely secure.
            </p>
          </section>

          <section>
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify
              users of significant changes through the platform. Continued use of
              Hippocampus Gaming after changes constitutes acceptance of the
              updated policy.
            </p>
          </section>
        </article>

        <footer className={styles.footer}>
          <p>&copy; 2026 Hippocampus Gaming. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Privacy;
